import React from 'react';

import {
  DeleteForever as DeleteIcon,
  Link as LinkIcon,
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, Grid, IconButton, Stack, Typography } from '@mui/material';

import { StripePaymentMethodType } from '@clearverify/constants';

import { Popover } from 'src/components/common';
import { SxProp } from 'src/types/mui';

import { AchPaymentMethodItem, BankCardPaymentMethodItem } from './components';
import type { PaymentMethodItemProps } from './types';

export const StripePaymentMethodItem: React.FC<PaymentMethodItemProps> = ({
  paymentMethod,
  isDefault,
  onSetDefault,
  onDetach,
}) => {
  const paymentMethodContent = React.useMemo(() => {
    const paymentMethodType = paymentMethod.type as StripePaymentMethodType;

    switch (paymentMethodType) {
      case StripePaymentMethodType.card:
        return <BankCardPaymentMethodItem paymentMethod={paymentMethod} />;
      case StripePaymentMethodType.ach:
        return <AchPaymentMethodItem paymentMethod={paymentMethod} />;
      default:
        return null;
    }
  }, [paymentMethod]);

  const renderPopoverContent = React.useCallback(
    ({ onClose }: { onClose: () => void }) => {
      const actionWithOnClose = (action: () => void, onClose: () => void) => () => {
        action();
        onClose();
      };

      return (
        <Stack alignItems="flex-start" p={1}>
          <Button
            variant="text"
            sx={buttonPopoverStyles}
            startIcon={<LinkIcon />}
            onClick={actionWithOnClose(onSetDefault, onClose)}
            disabled={isDefault}
            fullWidth
          >
            <Typography ml={1} color="text.primary" variant="body2">
              Set as default
            </Typography>
          </Button>
          <Button
            variant="text"
            sx={buttonDeletePopoverStyles}
            startIcon={<DeleteIcon />}
            onClick={actionWithOnClose(onDetach, onClose)}
            fullWidth
          >
            <Typography ml={1} variant="body2">
              Delete
            </Typography>
          </Button>
        </Stack>
      );
    },
    [isDefault, onDetach, onSetDefault],
  );

  return (
    <Grid sx={containerStyles(isDefault)} container alignItems="center">
      <Box display="flex" alignItems="center" gap={1}>
        {paymentMethodContent}
        <Chip
          variant={isDefault ? 'filled' : 'outlined'}
          size="small"
          color={isDefault ? 'success' : 'primary'}
          label={isDefault ? 'Default' : 'Connected'}
        />
      </Box>
      <Popover
        target={
          <IconButton sx={{ color: theme => theme.palette.action.active, m: 0, p: 0 }}>
            <MoreHorizIcon />
          </IconButton>
        }
        content={renderPopoverContent}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      />
    </Grid>
  );
};

const containerStyles =
  (isDefault: boolean | undefined): SxProp =>
  theme => ({
    border: `1px solid`,
    borderColor: isDefault ? theme.palette.success.main : theme.palette.slate?.[50],
    borderRadius: 1,
    flexFlow: 'row nowrap',
    backgroundColor: theme.palette.slate?.[50],
    justifyContent: 'space-between',
    padding: '8px 16px',
    '&:hover .controls-container': {
      visibility: 'visible',
    },
  });

const buttonPopoverStyles: SxProp = theme => ({
  color: theme.palette.slate?.[500],
  textTransform: 'none',
  justifyContent: 'flex-start',
});

const buttonDeletePopoverStyles: SxProp = theme => ({
  color: theme.palette.error.main,
  textTransform: 'none',
  justifyContent: 'flex-start',
});
